import { MantineProvider, MantineThemeOverride } from '@mantine/core';
import { ReactNode } from 'react';
import { createUiTheme } from '../ui.theme';

// must be imported in this order
import '../vars/primitive.vars.gen.css';

import '../vars/core.vars.gen.css';

import '../vars/dark.vars.gen.css';
import '../vars/light.vars.gen.css';

// The motion standard's curves and durations, which every expand, collapse and slide reads.
import '../../motion/motion.css';

export interface MantineThemeProviderProps {
  readonly children: ReactNode;
  readonly themeOverrides?: MantineThemeOverride;
  readonly defaultColorScheme?: 'light' | 'dark' | 'auto';
  /**
   * Makes Mantine follow a colour scheme the host owns, and keep following it.
   *
   * `defaultColorScheme` only sets the starting value. The console keeps its
   * scheme in Redux and writes it onto the document itself, so a subtree given
   * only a default keeps rendering the scheme it started with after the user
   * switches, and `useMantineColorScheme()` inside it reports the wrong one.
   */
  readonly forceColorScheme?: 'light' | 'dark';
  /**
   * Pass `"test"` from a test harness to switch off Mantine's transitions.
   *
   * Components that animate on open -- Drawer, Modal, Menu -- settle their focus trap at the
   * end of the transition, so under jsdom an interaction fired mid-animation is silently
   * swallowed and surfaces as "unable to find" the thing that was about to appear. Timing out
   * on a real duration makes those specs flaky rather than wrong. Mantine provides this switch
   * for exactly that; it has no effect outside tests.
   */
  readonly env?: 'default' | 'test';
}

export function MantineThemeProvider({
  children,
  themeOverrides,
  defaultColorScheme = 'light',
  forceColorScheme,
  env,
}: MantineThemeProviderProps) {
  const theme = createUiTheme(themeOverrides);

  return (
    <MantineProvider
      theme={theme}
      defaultColorScheme={defaultColorScheme}
      forceColorScheme={forceColorScheme}
      env={env}
    >
      {children}
    </MantineProvider>
  );
}
